// ═══════════════════════════════════════════════════════
// FORMATTERS — pure formatting functions
// Single source of truth for BRL, percentage, colors
//
// IMPORTANT: All exports use `export function` (not `export const`)
// because function declarations are HOISTED by the JS engine.
// This makes them immune to Temporal Dead Zone (TDZ) errors
// when Rollup reorders declarations in flat production bundles.
// ═══════════════════════════════════════════════════════
import { logError } from "./logger.js";

// ── Privacy Mode (module-level flag) ─────────────────
let _privacyMode = false;

/** Enable/disable privacy masking on all formatters */
export function setFormatterPrivacy(enabled: boolean): void {
  _privacyMode = !!enabled;
}

const MASK_BRL = "R$ •••••";
const MASK_PCT = "••••%";

// ── Safe converters ────────────────────────────────────

declare const window: any;

/**
 * Converts any value to text safe for JSX rendering.
 * React elements ($$typeof) pass through; unexpected objects are
 * logged and reported to Sentry if available.
 */

export function safeText(value: any, debugLabel = ""): string | React.ReactElement {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return typeof value === "string" ? value : String(value);
  }
  // React element — return directly
  if (typeof value === "object" && value !== null && value.$$typeof !== undefined) {
    return value;
  }
  // ⚠️ Unexpected object — convert to string and report
  logError("formatters", `Objeto detectado onde texto era esperado!`, {
    label: debugLabel,
    type: typeof value,
  });
  if (typeof window !== "undefined" && window.Sentry) {
    window.Sentry.captureMessage(`[FIX-310] Object as React child: ${debugLabel}`, {
      level: "warning",
      extra: { debugLabel, type: typeof value, valueJson: JSON.stringify(value) },
    });
  }
  return "";
}

/** Converts any value to safe number (never NaN/object) */

export function safeNum(v: any, fallback = 0): number {
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

// ── BRL Currency Formatters (unified) ──────────────────

/** Shared Intl formatter instance — reusable, no allocation per call */
const _brlFmt = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Format value as R$ with 2 decimal places (Intl-based, canonical) */

export function fmt(v: any): string {
  if (_privacyMode) return MASK_BRL;
  return _brlFmt.format(safeNum(v));
}

/** Alias — identical behavior. Prefer `fmt` for new code. */

export function fmtBRL(v: any): string {
  if (_privacyMode) return MASK_BRL;
  return fmt(v);
}

/** Alias for backward compat — identical to fmt. */

export function fmtR(v: any): string {
  if (_privacyMode) return MASK_BRL;
  return fmt(v);
}

/** Format value abbreviated: R$ 1,2M / R$ 45,3k / R$ 800 */

export function fmtK(v: any): string {
  if (_privacyMode) return MASK_BRL;
  const n = safeNum(v);
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(2).replace(".", ",")}M`;
  if (n >= 1e3) return `R$ ${(n / 1e3).toFixed(1).replace(".", ",")}k`;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

/** Format abbreviated with space: R$ 1,20 M / R$ 45,3 K */

export function fmtRK(v: any): string {
  if (_privacyMode) return MASK_BRL;
  const n = safeNum(v);
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(2).replace(".", ",")} M`;
  if (n >= 1e3) return `R$ ${(n / 1e3).toFixed(1).replace(".", ",")} K`;
  return `R$ ${Math.round(n).toLocaleString("pt-BR")}`;
}

// ── Percentage Formatters ──────────────────────────────

/** Format percentage with sign (+/-) — safe for undefined/null */

export function fmtP(v: any, d = 2): string {
  if (_privacyMode) return MASK_PCT;
  const n = safeNum(v);
  return `${n >= 0 ? "+" : ""}${n.toFixed(d).replace(".", ",")}%`;
}

// ── Semantic Color Functions ───────────────────────────

/** Text color for positive/negative values */

export function pc(v: any): string {
  return safeNum(v) >= 0 ? "#16A34A" : "#DC2626";
}

/** Background color for positive/negative values */

export function pb(v: any): string {
  return safeNum(v) >= 0 ? "#DCFCE7" : "#FEE2E2";
}
